/**
 * =============================================================================
 * Hook: usePlaybookTriggers
 * Versão: 1.0.0
 *
 * Descrição:
 *   Orquestra a persistência (diff) de gatilhos (bindings) de um Playbook a
 *   partir do próprio editor de playbook, reaproveitando as RPCs existentes:
 *     - upsert_pipeline_playbook_binding
 *     - delete_pipeline_playbook_binding
 *
 *   O hook NÃO cria RPC nova: recebe a lista desejada (`desired`) + a lista
 *   atual (`current`) e calcula em cliente o que precisa ser inserido,
 *   atualizado ou removido. Chama as RPCs de forma SEQUENCIAL para evitar
 *   deadlocks (mesma lição aplicada em handleInfoSave — DealDetailsModal).
 *
 * Release notes:
 *   v1.0.0 - Criação (mover configuração de gatilhos do Workspace para o
 *            próprio editor de Playbook, permitindo N regras por playbook).
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { CadenceTrigger } from '@/hooks/useWorkspacePlaybookBindings';

const logger = createLogger('usePlaybookTriggers');

// ----------------------------------------------------------------------------
// Tipos
// ----------------------------------------------------------------------------
export interface PlaybookTriggerDraft {
  /** UUID no banco quando editando; ausente para regras novas */
  id?: string | null;
  workspace_id: string;
  playbook_id: string;
  pipeline_id: string | null;
  phase_id: string | null;
  trigger: CadenceTrigger;
  cancel_previous: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface SaveTriggersInput {
  playbookId: string;
  workspaceId: string;
  /** Estado atual retornado pelo backend (para calcular remoções) */
  current: Array<{ id: string }>;
  /** Estado desejado pós-edição */
  desired: PlaybookTriggerDraft[];
}

// ----------------------------------------------------------------------------
// Hook
// ----------------------------------------------------------------------------
export function usePlaybookTriggers() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const mutation = useMutation({
    mutationFn: async ({ playbookId, workspaceId, current, desired }: SaveTriggersInput) => {
      // 1) Diff: quais IDs foram removidos
      const desiredIds = new Set(desired.map((d) => d.id).filter(Boolean) as string[]);
      const toDelete = current.filter((c) => !desiredIds.has(c.id)).map((c) => c.id);

      // 2) DELETE sequencial (evita deadlock em UPDATE massivo do mesmo tenant)
      for (const id of toDelete) {
        const { error } = await supabase.rpc('delete_pipeline_playbook_binding', {
          p_id: id,
          p_tenant_hint: tenantId,
        });
        if (error) {
          logger.error('Erro ao remover trigger', { id, error: error.message });
          throw error;
        }
      }

      // 3) UPSERT sequencial (insere novos e atualiza existentes)
      for (const d of desired) {
        const payload: Record<string, any> = {
          id: d.id ?? null,
          workspace_id: workspaceId,
          playbook_id: playbookId,
          pipeline_id: d.pipeline_id ?? null,
          // Só armazena phase_id em deal_enters_phase; nos demais triggers força NULL.
          phase_id: d.trigger === 'deal_enters_phase' ? d.phase_id : null,
          trigger: d.trigger,
          cancel_previous: !!d.cancel_previous,
          is_active: d.is_active !== false,
          sort_order: d.sort_order ?? 0,
        };
        const { error } = await supabase.rpc('upsert_pipeline_playbook_binding', {
          p_payload: payload as any,
          p_tenant_hint: tenantId,
        });
        if (error) {
          logger.error('Erro ao salvar trigger', { payload, error: error.message });
          throw error;
        }
      }

      return { deleted: toDelete.length, upserted: desired.length };
    },
    onSuccess: (_res, vars) => {
      // Refresh de listagens/detalhe e da aba do workspace
      queryClient.invalidateQueries({ queryKey: ['activity-playbook-details', tenantId, vars.playbookId] });
      queryClient.invalidateQueries({ queryKey: ['playbook-bindings', tenantId, vars.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['activity-playbooks', tenantId] });
    },
  });

  return {
    saveTriggers: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}
